import React, { Fragment, useState } from 'react';
import clsx from 'clsx';
import { IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from "@mui/material";
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import ReportProblemOutlined from '@mui/icons-material/ReportProblemOutlined';
import { idNamingConversion } from '../../../../../helpers/utilities';
import { STATUS, isDifferent, money, plural, rateLabel, round2, signedMoney, statusLabel } from './utils';

const AMOUNT_COLUMNS = [
    { key: 'invoiceAmount', label: 'Invoice Amount' },
    { key: 'gstAmount', label: 'GST Amount' },
    { key: 'cgst', label: 'CGST' },
    { key: 'sgst', label: 'SGST' },
    { key: 'igst', label: 'IGST' },
];
const COLUMN_COUNT = AMOUNT_COLUMNS.length + 4;

/* New value on top; the old one struck through with the difference underneath, only when it moved. */
const MoneyChange = ({ before, after, classes }) => {
    if (!isDifferent(before, after)) return money(after ?? before);
    return (
        <>
            <span className={classes.moneyNew}>{money(after)}</span>
            <span className={classes.moneyOld}>
                <span className={classes.moneyStrike}>{money(before)}</span>
                <span className={classes.delta}>{signedMoney(round2(after - before))}</span>
            </span>
        </>
    );
};

const StatusPill = ({ status, dryRun, classes }) => {
    const tone = status === 'CHANGED' && !dryRun ? 'saved' : STATUS[status]?.tone || 'neutral';
    const pill = <span className={clsx(classes.pill, classes[`tone_${tone}`])}>{statusLabel(status, dryRun)}</span>;
    return status === 'NOT_FOUND'
        ? <Tooltip title="No invoice with this number in your business" arrow>{pill}</Tooltip>
        : pill;
};

const LinesTable = ({ items, classes }) => (
    <>
        <p className={classes.detailsTitle}>Line changes ({items.length})</p>
        <div className={classes.linesTable}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Line</TableCell>
                        <TableCell>Product</TableCell>
                        <TableCell>Rate</TableCell>
                        <TableCell className={classes.num}>Line Amount</TableCell>
                        <TableCell className={classes.num}>GST Amount</TableCell>
                        <TableCell className={classes.num}>CGST</TableCell>
                        <TableCell className={classes.num}>SGST</TableCell>
                        <TableCell className={classes.num}>IGST</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {items.map((item, i) => (
                        <TableRow key={item.id || i}>
                            <TableCell>{item.lineNumber ?? i + 1}</TableCell>
                            <TableCell>{item.description || (item.productId ? `Product #${item.productId}` : '---')}</TableCell>
                            <TableCell style={{ whiteSpace: 'nowrap' }}>
                                {
                                    rateLabel(item.before) === rateLabel(item.after) ? rateLabel(item.after) :
                                        <>
                                            <span className={classes.moneyNew}>{rateLabel(item.after)}</span>
                                            <span className={clsx(classes.moneyOld, classes.moneyStrike)}>{rateLabel(item.before)}</span>
                                        </>
                                }
                            </TableCell>
                            {['amount', 'gstAmount', 'cgst', 'sgst', 'igst'].map((key) => (
                                <TableCell key={key} className={classes.num}>
                                    <MoneyChange before={item.before[key]} after={item.after[key]} classes={classes} />
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    </>
);

const SelfBillNote = ({ selfBill, dryRun, classes }) => {
    const parts = [];
    if (selfBill.headerChanged) parts.push('totals');
    if (selfBill.linesChanged) parts.push(plural(selfBill.linesChanged, 'line'));
    const name = `Sales Bill ${selfBill.serialNo || `#${selfBill.id}`}`;
    return (
        <p className={clsx(classes.note, classes.selfBillNote)}>
            {parts.length === 0 ? `${name} already matches.` : `${name}: ${parts.join(' and ')} ${dryRun ? 'will be' : 'were'} updated to match.`}
        </p>
    );
};

const Details = ({ result, dryRun, classes }) => (
    <>
        {result.selfBill && <SelfBillNote selfBill={result.selfBill} dryRun={dryRun} classes={classes} />}
        {
            result.error &&
            <div className={clsx(classes.callout, classes.tone_error)} role="alert">
                <ErrorOutline /><span>{result.error}</span>
            </div>
        }
        {
            result.warnings.length > 0 &&
            <div className={clsx(classes.callout, classes.tone_warning)}>
                <ReportProblemOutlined />
                <ul>{result.warnings.map((warning, i) => <li key={i}>{warning}</li>)}</ul>
            </div>
        }
        {result.items.length > 0 && <LinesTable items={result.items} classes={classes} />}
        {
            result.items.length === 0 && result.status === 'CHANGED' &&
            <p className={classes.note}>No line amounts or GST change; only stored totals are brought in line with the lines.</p>
        }
    </>
);

export const ResultTable = ({ results, dryRun, classes }) => {
    const [expanded, setExpanded] = useState({});
    const toggle = (key) => setExpanded((current) => ({ ...current, [key]: !current[key] }));

    return (
        <div className={classes.tableWrap}>
            <TableContainer>
                <Table stickyHeader size="small" aria-label="GST recalculation results">
                    <TableHead>
                        <TableRow>
                            <TableCell style={{ width: 40 }} />
                            <TableCell>Invoice No</TableCell>
                            <TableCell>Status</TableCell>
                            {AMOUNT_COLUMNS.map((column) => <TableCell key={column.key} className={classes.num}>{column.label}</TableCell>)}
                            <TableCell className={classes.num}>Lines Changed</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            results.length === 0 &&
                            <TableRow>
                                <TableCell colSpan={COLUMN_COUNT} style={{ textAlign: 'center', padding: 32 }} className={classes.note}>No invoices match this filter.</TableCell>
                            </TableRow>
                        }
                        {results.map((result, i) => {
                            const key = `${result.serialNo}_${result.invoiceId || i}`;
                            const hasDetails = result.items.length > 0 || result.warnings.length > 0 || !!result.error || result.status === 'CHANGED';
                            const open = !!expanded[key];
                            return (
                                <Fragment key={key}>
                                    <TableRow
                                        className={clsx(classes.row, { [classes.rowClickable]: hasDetails, [classes.rowOpen]: open })}
                                        onClick={() => hasDetails && toggle(key)}>
                                        <TableCell>
                                            {
                                                hasDetails &&
                                                <IconButton
                                                    size="small"
                                                    aria-label={open ? `Hide details of ${result.serialNo}` : `Show details of ${result.serialNo}`}
                                                    aria-expanded={open}
                                                    id={idNamingConversion(`Details ${result.serialNo}`, 'gstRecalculation', 'Button')}
                                                    onClick={(e) => { e.stopPropagation(); toggle(key); }}
                                                    className={classes.expandButton}>
                                                    {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                                </IconButton>
                                            }
                                        </TableCell>
                                        <TableCell className={classes.serial}>
                                            <div>{result.serialNo}</div>
                                            {result.selfBill?.serialNo && <div className={classes.subText}>Bill {result.selfBill.serialNo}</div>}
                                        </TableCell>
                                        <TableCell>
                                            {/* Wrapped so the card's `cell > span` block rule leaves the pill inline. */}
                                            <div className={classes.statusCell}>
                                                <StatusPill status={result.status} dryRun={dryRun} classes={classes} />
                                                {
                                                    result.warnings.length > 0 &&
                                                    <Tooltip title={`${plural(result.warnings.length, 'warning')} - open the row to read`} arrow>
                                                        <span className={classes.warnFlag}><ReportProblemOutlined />{result.warnings.length}</span>
                                                    </Tooltip>
                                                }
                                            </div>
                                        </TableCell>
                                        {AMOUNT_COLUMNS.map((column) => (
                                            <TableCell key={column.key} className={classes.num}>
                                                <MoneyChange before={result.before?.[column.key]} after={result.after?.[column.key]} classes={classes} />
                                            </TableCell>
                                        ))}
                                        <TableCell className={classes.num}>{result.status === 'NOT_FOUND' ? '---' : result.items.length}</TableCell>
                                    </TableRow>
                                    {
                                        open &&
                                        <TableRow>
                                            <TableCell colSpan={COLUMN_COUNT} className={classes.details}>
                                                <Details result={result} dryRun={dryRun} classes={classes} />
                                            </TableCell>
                                        </TableRow>
                                    }
                                </Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
};
